import { supabase } from '../lib/supabase';
import { logAuditEvent } from './auditLogger';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const TOKEN_KEY = 'investor_session_token'; // ✅ تم توحيد الاسم
const INVESTOR_KEY = 'investor_data';
const SESSION_TIMEOUT = 3600000; // 1 hour

export interface Investor {
  id: string;
  full_name: string;
  username: string;
  email: string;
  phone: string;
  status: string;
  total_investments: number;
  active_contracts: number;
  risk_profile: string;
}

// ✅ دالة تنظيف المدخلات
function sanitizeInput(input: string): string {
  return input.trim().replace(/[<>\"']/g, '');
}

// ✅ التحقق من انتهاء صلاحية الجلسة
function isSessionExpired(): boolean {
  const sessionData = localStorage.getItem('investor_session_time');
  if (!sessionData) return true;

  try {
    const loginTime = parseInt(sessionData, 10);
    return (Date.now() - loginTime) > SESSION_TIMEOUT;
  } catch {
    return true;
  }
}

export const investorAuthService = {
  // تسجيل الدخول مع حماية محسّنة
  async login(username: string, password: string): Promise<{ success: boolean; token?: string; investor?: Investor; error?: string }> {
    try {
      // تنظيف المدخلات
      const cleanUsername = sanitizeInput(username);
      const cleanPassword = password.trim();

      // التحقق من صحة المدخلات
      if (!cleanUsername || cleanUsername.length < 3) {
        return { success: false, error: 'اسم المستخدم غير صالح' };
      }

      if (!cleanPassword || cleanPassword.length < 6) {
        return { success: false, error: 'كلمة المرور قصيرة جداً' };
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/investor-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          username: cleanUsername, 
          password: cleanPassword 
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'فشل تسجيل الدخول' };
      }

      // حفظ التوكن ومعلومات المستثمر بشكل آمن
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(INVESTOR_KEY, JSON.stringify(data.investor));
      localStorage.setItem('investor_session_time', Date.now().toString());

      // تسجيل العملية
      await logAuditEvent({
        actorType: 'investor',
        actorId: data.investor.id,
        actorName: data.investor.full_name,
        actionType: 'read',
        entityType: 'profile',
        details: `تسجيل دخول المستثمر: ${data.investor.full_name}`,
      });

      return { success: true, token: data.token, investor: data.investor };
    } catch (error) {
      console.error('خطأ في تسجيل الدخول:', error);
      return { success: false, error: 'حدث خطأ في الاتصال' };
    }
  },

  // التحقق من الجلسة مع حماية محسّنة
  async verifySession(): Promise<{ success: boolean; investor?: Investor; error?: string }> {
    try {
      // التحقق من انتهاء صلاحية الجلسة
      if (isSessionExpired()) {
        this.logout();
        return { success: false, error: 'انتهت صلاحية الجلسة' };
      }

      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        return { success: false, error: 'لا توجد جلسة نشطة' };
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/investor-verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        this.logout();
        return { success: false, error: data.error || 'جلسة غير صالحة' };
      }

      // تحديث معلومات المستثمر
      localStorage.setItem(INVESTOR_KEY, JSON.stringify(data.investor));

      return { success: true, investor: data.investor };
    } catch (error) {
      console.error('خطأ في التحقق من الجلسة:', error);
      this.logout();
      return { success: false, error: 'حدث خطأ في التحقق' };
    }
  },

  // تسجيل الخروج مع تنظيف شامل
  async logout(): Promise<void> {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        await fetch(`${SUPABASE_URL}/functions/v1/investor-logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });
      }
    } catch (error) {
      console.error('خطأ في تسجيل الخروج:', error);
    } finally {
      // مسح جميع البيانات المحلية
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(INVESTOR_KEY);
      localStorage.removeItem('investor_session_time');
      
      // مسح أي بيانات حساسة أخرى
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('investor_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  },

  // الحصول على التوكن
  getToken(): string | null {
    try {
      if (isSessionExpired()) {
        this.logout();
        return null;
      }
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },

  // الحصول على معلومات المستثمر
  getInvestor(): Investor | null {
    try {
      const investorData = localStorage.getItem(INVESTOR_KEY);
      return investorData ? JSON.parse(investorData) : null;
    } catch (error) {
      console.error('خطأ في قراءة بيانات المستثمر:', error);
      return null;
    }
  },

  // التحقق من تسجيل الدخول
  isAuthenticated(): boolean {
    try {
      if (isSessionExpired()) {
        return false;
      }
      return !!this.getToken();
    } catch {
      return false;
    }
  },
};
